import { Injectable } from '@angular/core';
import { Observable, asapScheduler, of } from 'rxjs';
import { catchError, map, observeOn, shareReplay, tap } from 'rxjs/operators';

import { ApiService } from 'src/app/core/services/api.service';
import { SessionScopedState } from 'src/app/core/services/session/session-scoped-state';
import {
  DirectAddress,
  DirectAddressCheck,
  DirectConnectionStatus,
  DirectShipmentHistoryView,
  DirectTransferView,
  FailedDeliveryView,
} from '../dormotech-direct.types';

/**
 * Dormo Direct on a lab that sends patients to the Direct lab: whether a
 * department may create Direct patients, and whether the Direct lab can ship a
 * device to a patient's address.
 */
@Injectable({ providedIn: 'root' })
export class DormotechDirectService extends ApiService implements SessionScopedState {
  /**
   * Whether a department has Dormo Direct switched on.
   *
   * Same contract as the research-consent lookup next to it: answers are
   * remembered for the session, concurrent callers share one request, a
   * remembered answer still arrives asynchronously, and a failure is not
   * remembered and reads as off — the backend refuses a Direct patient in a
   * department without it regardless of what the form showed.
   */
  public isEnabledForDepartment(departmentId: string | number): Observable<boolean> {
    if (!departmentId) {
      return of(false);
    }

    const key = String(departmentId);
    const known = this.enabledByDepartment.get(key);
    if (known !== undefined) {
      return of(known).pipe(observeOn(asapScheduler));
    }

    const pending = this.enabledInFlight.get(key);
    if (pending) {
      return pending;
    }

    const request = this.request({ path: `departments/${key}`, method: 'GET' }).pipe(
      map((department) => !!department?.dormotechDirectEnabled),
      tap((enabled) => {
        this.enabledByDepartment.set(key, enabled);
        this.enabledInFlight.delete(key);
      }),
      catchError(() => {
        this.enabledInFlight.delete(key);
        return of(false);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.enabledInFlight.set(key, request);
    return request;
  }

  /** Whether the Direct lab can ship a device to a US address and get it back. */
  public checkAddress(address: DirectAddress): Observable<DirectAddressCheck> {
    return this.request({
      path: 'dormotech-direct/address-check',
      method: 'POST',
      body: {
        line1: address.line1,
        line2: address.line2,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        countryAlpha2: 'US',
      },
    });
  }

  /**
   * Whether this lab is connected to the Dormo Direct lab. Asked once and shared;
   * `refresh` asks again — after connecting, or when a page needs it current.
   * A failure reads as not connected and is not remembered.
   */
  public connectionStatus(refresh = false): Observable<DirectConnectionStatus> {
    if (!this.connection || refresh) {
      this.connection = this.request({ path: 'dormotech-direct/connection', method: 'GET' }).pipe(
        catchError(() => {
          this.connection = undefined;
          return of(UNREADABLE);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.connection;
  }

  /**
   * Connects this lab with the secret from the Direct lab's Labs list. Given the
   * department whose Direct Enabled tick asked for the secret, Direct is switched
   * on there in the same step.
   */
  public connect(secret: string, departmentId?: number | string | null): Observable<DirectConnectionStatus> {
    const body = departmentId ? { secret, departmentId: Number(departmentId) } : { secret };
    return this.request({ path: 'dormotech-direct/connection', method: 'POST', body }).pipe(
      tap((status: DirectConnectionStatus) => {
        this.connection = of(status);
        if (departmentId) {
          this.enabledByDepartment.delete(String(departmentId));
        }
      }),
    );
  }

  /**
   * Forget every department answer and the connection, so the next question is
   * asked again. A department's switch and the lab's connection are changed on
   * the department page; the New Patient dialog calls this when it opens, so it
   * never offers — or hides — Direct on an answer from before a change.
   */
  /** Whether a Direct patient reached Dormotech Direct, and why not when it refused them. */
  public transfer(patientId: number | string): Observable<DirectTransferView> {
    return this.request({ path: `patients/${patientId}/direct-transfer`, method: 'GET' });
  }

  /** Where a Direct test's device has been, as Dormotech Direct last reported. */
  public shipmentHistory(testId: number | string): Observable<DirectShipmentHistoryView> {
    return this.request({ path: `tests/${testId}/direct-shipment-history`, method: 'GET' });
  }

  /** Sends again what failed to reach Dormotech Direct for a Direct patient: the patient, a change, a test. */
  public retryTransfer(patientId: number | string): Observable<DirectTransferView> {
    return this.request({ path: `patients/${patientId}/direct-transfer`, method: 'POST' });
  }

  /** On the Direct lab: what could not be sent to a Direct patient's lab after every attempt. */
  public failedDeliveries(patientId: number | string): Observable<{ failedDeliveries: FailedDeliveryView[] }> {
    return this.request({ path: `patients/${patientId}/direct-deliveries`, method: 'GET' });
  }

  /** On the Direct lab: sends it all again, with its attempts back. */
  public retryDeliveries(patientId: number | string): Observable<{ retried: number }> {
    return this.request({ path: `patients/${patientId}/direct-deliveries/retry`, method: 'POST' });
  }

  public forgetAnswers(): void {
    this.enabledByDepartment.clear();
    this.enabledInFlight.clear();
    this.connection = undefined;
  }

  /** Forget every department answer, in-flight ones too, when the user changes. */
  public clearSessionState(): void {
    this.forgetAnswers();
  }

  private readonly enabledByDepartment = new Map<string, boolean>();
  private readonly enabledInFlight = new Map<string, Observable<boolean>>();
  private connection?: Observable<DirectConnectionStatus>;
}

const UNREADABLE: DirectConnectionStatus = {
  connected: false,
  state: 'UNKNOWN',
  directHost: null,
  connectedAt: null,
  rejectedAt: null,
};
